import { useTranslation } from 'react-i18next';
import { StyledCheckAllWrapper, StyledRotatingIconButton } from '../../styles';
import {
  StyledAutomationCheckbox,
  StyledAutomationsGroupContent,
  StyledAutomationsGroupContentInner,
} from './styles';
import {
  type Dispatch,
  type FC,
  type SetStateAction,
  useMemo,
  useState,
} from 'react';
import Icon, { IconSizing } from '__components/Kizen/Icon';
import { colorsButton } from 'app/colors';
import Checkbox from '__components/Inputs/CheckboxGroup/Checkbox';
import { ApplyAllCheckbox } from '../ApplyAllCheckbox';
import {
  getAllFolderIdsInNode,
  type AutomationNode,
  type AutomationOption,
} from './helpers';
import { AutomationFolderNode } from './AutomationFolderNode';

export const AutomationsExportGroup: FC<{
  label: string;
  allAutomations: AutomationOption[];
  rootAutomations: AutomationOption[];
  folderTree: AutomationNode[];
  selectedAutomations: AutomationOption[];
  setSelectedAutomations: Dispatch<SetStateAction<AutomationOption[]>>;
  selectedFolders: string[];
  setSelectedFolders: Dispatch<SetStateAction<string[]>>;
}> = ({
  label,
  allAutomations,
  rootAutomations,
  folderTree,
  selectedAutomations,
  setSelectedAutomations,
  selectedFolders,
  setSelectedFolders,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const selectedAutomationsSet = useMemo(
    () => new Set(selectedAutomations.map((a) => a.value)),
    [selectedAutomations]
  );

  const selectedFolderIds = useMemo(
    () => new Set(selectedFolders),
    [selectedFolders]
  );

  const selectAll = () => {
    setSelectedAutomations(allAutomations);
    const allFolderIds = folderTree.flatMap((folder) =>
      getAllFolderIdsInNode(folder)
    );
    setSelectedFolders(allFolderIds);
  };

  const handleRootAutomationChange = (
    automation: AutomationOption,
    checked: boolean
  ) => {
    if (checked) {
      setSelectedAutomations((prev) => {
        if (prev.some((a) => a.value === automation.value)) return prev;
        return [...prev, automation];
      });
    } else {
      setSelectedAutomations((prev) =>
        prev.filter((a) => a.value !== automation.value)
      );
    }
  };

  return (
    <>
      <StyledCheckAllWrapper data-qa-label={label}>
        <ApplyAllCheckbox
          label={`${label} (${selectedAutomations.length} / ${allAutomations.length})`}
          onCheck={selectAll}
        />
        <StyledRotatingIconButton
          sizing="dense"
          color={colorsButton.iconGray}
          onClick={() => setExpanded(!expanded)}
          expanded={expanded}
          title={expanded ? t('Collapse') : t('Expand')}
        >
          <IconSizing size="14px">
            <Icon icon="chevron-down" />
          </IconSizing>
        </StyledRotatingIconButton>
      </StyledCheckAllWrapper>
      <StyledAutomationsGroupContent
        data-qa-label={`${label} options`}
        expanded={expanded}
      >
        <StyledAutomationsGroupContentInner>
          {folderTree.map((folder) => (
            <AutomationFolderNode
              key={folder.id}
              node={folder}
              depth={0}
              selectedAutomations={selectedAutomationsSet}
              selectedFolderIds={selectedFolderIds}
              setSelectedAutomations={setSelectedAutomations}
              setSelectedFolders={setSelectedFolders}
            />
          ))}
          {rootAutomations.map((automation) => (
            <StyledAutomationCheckbox key={automation.value}>
              <Checkbox
                checked={selectedAutomationsSet.has(automation.value)}
                label={automation.label}
                onChange={(checked: boolean) =>
                  handleRootAutomationChange(automation, checked)
                }
                data-qa={`automation-checkbox-${automation.value}`}
              />
            </StyledAutomationCheckbox>
          ))}
        </StyledAutomationsGroupContentInner>
      </StyledAutomationsGroupContent>
    </>
  );
};
